"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import * as Sentry from "@sentry/nextjs"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { getMessageFile } from "./config"
import { MergeMessagesProvider } from "./merge-messages-provider"
import type { Messages } from "./pick-messages"
import type { LazyMessageBundle } from "./route-namespaces"

/**
 * Renders `children` once the messages of `bundle` are loaded, and starts
 * loading them when `load` turns true.
 *
 * For a panel that is mounted on most pages but rarely opened: its messages
 * would otherwise sit in every page's HTML. The bundle's contents are
 * generated (`LAZY_MESSAGE_BUNDLES`) and served by
 * `app/api/messages/[messageFile]/[bundle]`.
 *
 * A failed load is reported to Sentry and handed to `onError`, which must turn
 * `load` off again: the next time it turns true the bundle is fetched afresh,
 * so the control that opens the panel retries instead of going dead.
 */
export function LazyMessages({
  bundle,
  load,
  onError,
  children,
}: {
  bundle: LazyMessageBundle
  load: boolean
  onError: () => void
  children: ReactNode
}) {
  const messageFile = getMessageFile(useLocale(), useCountry().code)
  const [messages, setMessages] = useState<Messages | null>(null)
  // Read through a ref so a new callback identity does not restart the fetch.
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onErrorRef.current = onError
  })

  useEffect(() => {
    if (!load || messages) return
    const controller = new AbortController()
    fetch(`/api/messages/${messageFile}/${bundle}`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `lazy messages ${messageFile}/${bundle}: HTTP ${response.status}`
          )
        }
        return response.json() as Promise<Messages>
      })
      .then(setMessages)
      .catch((error: unknown) => {
        // Unmounted or re-run mid-flight: this effect's own cleanup, not a failure.
        if (controller.signal.aborted) return
        Sentry.captureException(error, {
          tags: { surface: "lazy-messages", bundle, messageFile },
        })
        onErrorRef.current()
      })
    return () => controller.abort()
  }, [load, messages, messageFile, bundle])

  if (!messages) return null
  return (
    <MergeMessagesProvider messages={messages}>
      {children}
    </MergeMessagesProvider>
  )
}
